import { useState } from "react";
import { inviteUserRequestSchema } from "@/validation/invite-user-request";
import { zodToFieldErrors, firstFieldErrors } from "@trillion/validation";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import { Badge } from "@trillion/ui/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import { Loader2, Plus, X } from "lucide-react";
import { useProducts } from "@/hooks/use-products";
import type { InviteUserRequest } from "@/types";

const ROLES = ["admin", "user", "editor"];

interface SelectedRole {
  product: string;
  role: string;
}

interface InviteUserFormProps {
  onSubmit: (data: InviteUserRequest) => Promise<void>;
  isLoading: boolean;
}

export function InviteUserForm({ onSubmit, isLoading }: InviteUserFormProps) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [roles, setRoles] = useState<SelectedRole[]>([]);
  const [newProduct, setNewProduct] = useState("");
  const [newRole, setNewRole] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { products, isLoading: productsLoading } = useProducts();

  function clearError(field: string) {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  function addRole() {
    if (!newProduct || !newRole) return;
    const exists = roles.some(
      (r) => r.product === newProduct && r.role === newRole
    );
    if (!exists) {
      setRoles((prev) => [...prev, { product: newProduct, role: newRole }]);
    }
    setNewProduct("");
    setNewRole("");
  }

  function removeRole(target: SelectedRole) {
    setRoles((prev) =>
      prev.filter((r) => !(r.product === target.product && r.role === target.role))
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const payload: InviteUserRequest = {
      email: email.trim(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      roles: roles.map((r) => `${r.product}:${r.role}`),
    };

    const result = inviteUserRequestSchema.safeParse(payload);
    if (!result.success) {
      setErrors(firstFieldErrors(zodToFieldErrors(result.error)));
      return;
    }

    await onSubmit(payload);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite User</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="first_name">First name *</Label>
              <Input
                id="first_name"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  clearError("first_name");
                }}
                placeholder="Ada"
              />
              {errors.first_name && (
                <p className="text-sm text-destructive">{errors.first_name}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="last_name">Last name *</Label>
              <Input
                id="last_name"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  clearError("last_name");
                }}
                placeholder="Lovelace"
              />
              {errors.last_name && (
                <p className="text-sm text-destructive">{errors.last_name}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearError("email");
              }}
              placeholder="ada@example.com"
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email}</p>
            )}
          </div>

          {/* Roles */}
          <div className="flex flex-col gap-3">
            <Label>Roles</Label>
            {roles.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {roles.map((r) => (
                  <Badge
                    key={`${r.product}:${r.role}`}
                    variant="secondary"
                    className="gap-1 pr-1"
                  >
                    {r.product}:{r.role}
                    <button
                      type="button"
                      onClick={() => removeRole(r)}
                      className="rounded-sm hover:bg-muted-foreground/20"
                      aria-label={`Remove ${r.product}:${r.role}`}
                    >
                      <X className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No roles selected. Roles can also be added later.
              </p>
            )}

            <div className="flex items-end gap-3">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-muted-foreground">Product</span>
                <Select value={newProduct} onValueChange={setNewProduct}>
                  <SelectTrigger className="w-[140px]" disabled={productsLoading}>
                    <SelectValue
                      placeholder={productsLoading ? "Loading products…" : "Select product"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.slug} value={p.slug}>
                        {p.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-muted-foreground">Role</span>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={addRole}
                disabled={!newProduct || !newRole}
                size="sm"
              >
                <Plus className="mr-1 size-4" />
                Add role
              </Button>
            </div>
            {errors.roles && (
              <p className="text-sm text-destructive">{errors.roles}</p>
            )}
          </div>

          <div className="flex gap-3">
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
              Send invite
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
